import { generateUUID } from "@perceptron/utils/crypto";
import { MediaKind, DocumentVersion } from "@perceptron/utils/types";
import { create } from "zustand";

import { trackMedia } from "@demo/libs/analytics/utils";
import { MediaFactory } from "@demo/libs/media-factory";
import { Role, MediaData } from "@demo/types/base";
import { MediaSet } from "@demo/types/media";

export const DEFAULT_MEDIA_SET: MediaSet = {
  id: "default-media-set",
  media: [],
  role: Role.USER,
  documentVersion: DocumentVersion.Draft,
  documentIndex: 0,
};

/**
 * The target media: the last item of the latest set that holds any. An empty set after it (a container
 * added and not yet filled) does not clear it: the footer's toggles key off this, and would all lock while
 * the prompt still has an image and Run is enabled.
 */
export const getLastMedia = (mediaSets: MediaSet[]): MediaData | undefined => {
  for (let i = mediaSets.length - 1; i >= 0; i--) {
    const media = mediaSets[i]?.media ?? [];
    if (media.length > 0) return media[media.length - 1];
  }
  return undefined;
};

interface DraftMediaSetTombstone {
  mediaSet: MediaSet;
  index: number;
}

const removedDraftMediaSets = new Map<string, DraftMediaSetTombstone>();
const removedMedia = new Map<string, Map<string, MediaData>>();

function isEditableDraftSet(mediaSet: MediaSet, role: Role) {
  return (
    mediaSet.role === role && mediaSet.documentVersion === DocumentVersion.Draft && mediaSet.id !== DEFAULT_MEDIA_SET.id
  );
}

export interface MediaStore {
  targetMedia: MediaData | undefined;
  mediaSets: MediaSet[];
  addMediaSet: (role: Role, documentVersion: DocumentVersion, documentIndex?: number) => string;
  removeMediaSet: (setId: string) => void;
  addMedia: (setId: string, mediaData: MediaData) => void;
  removeMedia: (setId: string, index: number) => void;
  removeMediaById: (setId: string, mediaId: string) => void;
  syncDraftMediaSetsFromDocument: (role: Role, mediaSets: Array<{ id: string; mediaIds: string[] }>) => void;
  hasUndoTombstones: (role: Role) => boolean;
  pruneUndoTombstones: (role: Role, historyMediaIds: ReadonlyMap<string, ReadonlySet<string>> | null) => void;
  getUserMediaSet: () => MediaSet[];
  getSystemMediaSet: () => MediaSet[];
  getDraftMediaSets: () => MediaSet[];
  getLastRunMediaSets: () => MediaSet[];
  clearMediaSets: () => void;
  // Media kind detection helpers
  hasMediaOfKind: (kind: MediaKind) => boolean;
  getMediaByKind: (kind: MediaKind) => MediaData[];
}

export const useMediaStore = create<MediaStore>((set, get) => ({
  targetMedia: undefined,
  mediaSets: [DEFAULT_MEDIA_SET],

  addMediaSet: (role: Role, documentVersion: DocumentVersion, documentIndex?: number) => {
    const setId = `${role}-${generateUUID()}`;
    const newMediaSet: MediaSet = {
      id: setId,
      media: [],
      role,
      documentVersion,
      documentIndex,
    };

    set((state) => {
      const updatedMediaSets = [...state.mediaSets, newMediaSet];
      return {
        mediaSets: updatedMediaSets,
        targetMedia: getLastMedia(updatedMediaSets),
      };
    });

    return setId;
  },

  removeMediaSet: (setId: string) => {
    removedDraftMediaSets.delete(setId);
    removedMedia.delete(setId);
    set((state) => {
      const updatedMediaSets = state.mediaSets.filter((set) => set.id !== setId);

      return {
        mediaSets: updatedMediaSets,
        targetMedia: getLastMedia(updatedMediaSets),
      };
    });
  },

  addMedia: (setId: string, mediaData: MediaData) => {
    set((state) => {
      const updatedMediaSets = state.mediaSets.map((set) =>
        set.id === setId ? { ...set, media: [...set.media, mediaData] } : set,
      );

      return {
        mediaSets: updatedMediaSets,
        targetMedia: getLastMedia(updatedMediaSets),
      };
    });
  },

  removeMedia: (setId: string, index: number) => {
    const mediaItem = get().mediaSets.find((mediaSet) => mediaSet.id === setId)?.media[index];
    if (mediaItem) get().removeMediaById(setId, mediaItem.key);
  },

  removeMediaById: (setId: string, mediaId: string) => {
    set((state) => {
      const mediaSet = state.mediaSets.find((set) => set.id === setId);
      const mediaItem = mediaSet?.media.find((item) => item.key === mediaId);
      if (!mediaSet || !mediaItem) return state;

      const mediaKind = MediaFactory.detectMediaKind(mediaItem);
      trackMedia.delete(mediaItem.key, mediaKind);

      const removedForSet = removedMedia.get(setId) ?? new Map<string, MediaData>();
      removedForSet.set(mediaItem.key, mediaItem);
      removedMedia.set(setId, removedForSet);

      const updatedMediaSets = state.mediaSets.map((set) =>
        set.id === setId ? { ...set, media: set.media.filter((item) => item.key !== mediaId) } : set,
      );

      return {
        mediaSets: updatedMediaSets,
        targetMedia: getLastMedia(updatedMediaSets),
      };
    });
  },

  syncDraftMediaSetsFromDocument: (role, documentMediaSets) => {
    const seenSetIds = new Set<string>();
    const uniqueDocumentMediaSets = documentMediaSets.filter(({ id }) => {
      if (seenSetIds.has(id)) return false;
      seenSetIds.add(id);
      return true;
    });
    const orderedIds = new Set(uniqueDocumentMediaSets.map((mediaSet) => mediaSet.id));

    set((state) => {
      const editable = state.mediaSets
        .map((mediaSet, index) => ({ mediaSet, index }))
        .filter(({ mediaSet }) => isEditableDraftSet(mediaSet, role));

      editable.forEach(({ mediaSet, index }) => {
        if (!orderedIds.has(mediaSet.id)) {
          removedDraftMediaSets.set(mediaSet.id, { mediaSet, index });
        }
      });

      const tombstoneIndexes = new Map(
        uniqueDocumentMediaSets.flatMap(({ id }) => {
          const tombstone = removedDraftMediaSets.get(id);
          return tombstone ? [[id, tombstone.index] as const] : [];
        }),
      );

      const orderedDraftSets = uniqueDocumentMediaSets.flatMap(({ id, mediaIds }) => {
        const activeSet = state.mediaSets.find((mediaSet) => mediaSet.id === id);
        const tombstone = removedDraftMediaSets.get(id);
        const source = activeSet ?? tombstone?.mediaSet;
        if (!source || !isEditableDraftSet(source, role)) return [];

        removedDraftMediaSets.delete(id);
        const uniqueMediaIds = [...new Set(mediaIds)];
        const desiredMediaIds = new Set(uniqueMediaIds);
        const mediaById = new Map(source.media.map((item) => [item.key, item]));
        const removedForSet = removedMedia.get(id);
        source.media.forEach((item) => {
          if (!desiredMediaIds.has(item.key)) removedForSet?.set(item.key, item);
        });

        const orderedMedia = uniqueMediaIds.flatMap((mediaId) => {
          const activeItem = mediaById.get(mediaId);
          if (activeItem) {
            removedForSet?.delete(mediaId);
            return [activeItem];
          }
          const restoredItem = removedForSet?.get(mediaId);
          if (restoredItem) {
            removedForSet?.delete(mediaId);
            return [restoredItem];
          }
          return [];
        });
        if (removedForSet?.size === 0) removedMedia.delete(id);

        return [{ ...source, media: orderedMedia }];
      });

      const otherSets = state.mediaSets.filter((mediaSet) => !isEditableDraftSet(mediaSet, role));
      const priorIndexes = [...editable.map(({ index }) => index), ...tombstoneIndexes.values()];
      const insertionIndex = priorIndexes.length > 0 ? Math.min(...priorIndexes) : state.mediaSets.length;
      const insertionOffset = state.mediaSets
        .slice(0, insertionIndex)
        .filter((mediaSet) => !isEditableDraftSet(mediaSet, role)).length;
      const updatedMediaSets = [
        ...otherSets.slice(0, insertionOffset),
        ...orderedDraftSets,
        ...otherSets.slice(insertionOffset),
      ];

      const unchanged =
        updatedMediaSets.length === state.mediaSets.length &&
        updatedMediaSets.every((mediaSet, index) => {
          const previous = state.mediaSets[index];
          return (
            mediaSet === previous ||
            (mediaSet?.id === previous?.id &&
              mediaSet?.media.length === previous?.media.length &&
              mediaSet?.media.every((item, itemIndex) => item === previous?.media[itemIndex]))
          );
        });
      if (unchanged) return state;

      return {
        mediaSets: updatedMediaSets,
        targetMedia: getLastMedia(updatedMediaSets),
      };
    });
  },

  hasUndoTombstones: (role) => {
    const activeSetRoles = new Map(get().mediaSets.map((mediaSet) => [mediaSet.id, mediaSet.role]));
    if ([...removedDraftMediaSets.values()].some(({ mediaSet }) => mediaSet.role === role)) return true;

    return [...removedMedia.keys()].some(
      (setId) => (activeSetRoles.get(setId) ?? removedDraftMediaSets.get(setId)?.mediaSet.role) === role,
    );
  },

  pruneUndoTombstones: (role, historyMediaIds) => {
    if (!historyMediaIds) return;

    const activeSetRoles = new Map(get().mediaSets.map((mediaSet) => [mediaSet.id, mediaSet.role]));
    for (const [setId, tombstone] of removedDraftMediaSets) {
      if (tombstone.mediaSet.role === role && !historyMediaIds.has(setId)) removedDraftMediaSets.delete(setId);
    }

    for (const [setId, removedForSet] of removedMedia) {
      const setRole = activeSetRoles.get(setId) ?? removedDraftMediaSets.get(setId)?.mediaSet.role;
      if (setRole !== role) continue;
      const retainedMediaIds = historyMediaIds.get(setId);
      if (!retainedMediaIds) {
        removedMedia.delete(setId);
        continue;
      }

      for (const mediaId of removedForSet.keys()) {
        if (!retainedMediaIds.has(mediaId)) removedForSet.delete(mediaId);
      }
      if (removedForSet.size === 0) removedMedia.delete(setId);
    }
  },

  getUserMediaSet: () => get().mediaSets.filter((set) => set.role === Role.USER),

  getSystemMediaSet: () => get().mediaSets.filter((set) => set.role === Role.SYSTEM),

  getDraftMediaSets: () => get().mediaSets.filter((set) => set.documentVersion === DocumentVersion.Draft),

  getLastRunMediaSets: () => get().mediaSets.filter((set) => set.documentVersion === DocumentVersion.LastRun),

  clearMediaSets: () => {
    removedDraftMediaSets.clear();
    removedMedia.clear();
    set({
      mediaSets: [DEFAULT_MEDIA_SET],
      targetMedia: undefined,
    });
  },

  // Media kind detection helpers
  hasMediaOfKind: (kind: MediaKind) => {
    const userMediaSets = get().getUserMediaSet();
    return userMediaSets.some((set) => set.media.some((item) => MediaFactory.detectMediaKind(item) === kind));
  },

  getMediaByKind: (kind: MediaKind) => {
    const userMediaSets = get().getUserMediaSet();
    return userMediaSets.flatMap((set) => set.media.filter((item) => MediaFactory.detectMediaKind(item) === kind));
  },
}));

export const {
  addMediaSet,
  removeMediaSet,
  addMedia,
  removeMedia,
  removeMediaById,
  getUserMediaSet,
  getSystemMediaSet,
  getDraftMediaSets,
  getLastRunMediaSets,
  clearMediaSets,
} = useMediaStore.getState();
